import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./Sidebar.css";
import {
  NotificationIcon,
  SecureHomeIcon,
  CalendarIcon,
  UsersGroupIcon,
  SecureCameraIcon,
  UserProfileCircleIcon,
  BellNotificationIcon,
  BookOpenQuestionIcon,
  ArrowRightRedIcon,
  PlusCircleBlackIcon,
  CompassIcon, 
} from "../../helper/icon";
import { useAuth } from "../../context/auth";

const Sidebar = () => {
  const location = useLocation();
  const { pathname } = location;
  const { user, logout } = useAuth();
  const [showLogoutModal, setShowLogoutModal] = React.useState(false);
  const navigate = useNavigate();
  const userName =
    location.state?.userName ||
    (user?.firstName
      ? `${user.firstName} ${user.lastName || ""}`.trim()
      : "User");

  const selectedRole = user?.activeRole;

  const isMenuItemActive = (path) => {
    if (path === `/home/${selectedRole}`) {
      return pathname === path || pathname === "/";
    }
    return pathname === path;
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch (error) {
    }
  };

  const ExploreIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
    </svg>
  );

  const menuItems = [
    {
      id: 1,
      icon: <SecureHomeIcon />,
      label: "Home",
      path: `/home/${selectedRole}`,
      visible: true,
    },
    {
      id: 2,
      icon: <ExploreIcon />,
      label: "Explore Events",
      path: "/explore",
      visible: true, 
    },
    {
      id: 3,
      icon: <CalendarIcon />,
      label: "Event Management",
      path: "/eventManagement",
      visible: selectedRole === "host",
    },
    {
      id: 4,
      icon: <PlusCircleBlackIcon />,
      label:
        selectedRole === "host"
          ? "Create Event"
          : selectedRole === "usher"
            ? "Join as an Usher"
            : "Join Event",
      path: selectedRole === "host" ? "/createEvent" : "/joinEvent",
      visible: true,
    },
    {
      id: 5,
      icon: <UsersGroupIcon />,
      label: "Invites",
      path:
        selectedRole === "guest" || selectedRole === "usher"
          ? "/invites"
          : null,
      visible: selectedRole === "guest" || selectedRole === "usher",
    },
    {
      id: 6,
      icon: <UserProfileCircleIcon />,
      label: "Account",
      path: "/account",
      visible: true,
    },
    {
      id: 7,
      icon: <BellNotificationIcon />,
      label: "Notification",
      path: "/notifications",
      visible: true,
    },
    {
      id: 8,
      icon: <BookOpenQuestionIcon />,
      label: "Help Desk & Support",
      path: "/HelpDesk",
      visible: true,
    },
  ];

  const getInitials = (name) => {
    const names = name.split(" ").filter(Boolean);
    const initials = names.map((name) => name[0]?.toUpperCase() || "");
    return initials.join("");
  };

  const capitalizeWords = (str) =>
    str.replace(/\b\w/g, (char) => char.toUpperCase());

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <h1 className="logo-sidebar">
          <img
            src={"/images/logoOnBlack.png"}
            alt="Logo"
            onClick={() => navigate("/")}
          />
        </h1>
        <span className="notification-icon material-icons">
          <NotificationIcon />
        </span>
      </div>
      <span className="sidebar-login-status">
        You are logged in as{" "}
        <strong>{capitalizeWords(user?.activeRole || "")}</strong>
      </span>

      <div className="sidebar-search"></div>
      <nav className="sidebar-nav">
        <ul>
          {menuItems
            .filter((item) => {
              return item.visible;
            })
            .map((item) => {
              const isActive = isMenuItemActive(item.path);
              return (
                <li key={item.id} className={isActive ? "active" : ""}>
                  <Link to={item.path}>
                    <span className="material-icons">{item.icon}</span>
                    <span>{item.label}</span>
                  </Link>
                </li>
              );
            })}
        </ul>
      </nav>
      <div className="user-profile">
        <div className="avatar">
          <span className="initials">{getInitials(userName || "User")}</span>
        </div>
        <div className="user-info">
          <p className="user-name">{userName}</p>
          <button
            onClick={() => setShowLogoutModal(true)}
            className="logout-button"
          >
            <span className="material-icons">
              <ArrowRightRedIcon />
            </span>
            Logout
          </button>
        </div>
      </div>

      {showLogoutModal && (
        <div className="modal-backdrop">
          <div className="logout-modal">
            <h2>Confirm Logout</h2>
            <p>Are you sure you want to log out?</p>
            <div className="modal-actions">
              <button
                className="confirm-logout"
                onClick={() => {
                  handleLogout();
                  setShowLogoutModal(false);
                }}
              >
                Yes, Logout
              </button>
              <button
                className="cancel-logout"
                onClick={() => setShowLogoutModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;